import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {IDS} from '@github-ui/item-picker-base/constants/ids'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {SELECTORS} from '@github-ui/item-picker-base/constants/selectors'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {
  ProjectPickerBase,
  type ProjectPickerBaseProps,
  type ProjectPickerBaseProject,
} from '@github-ui/item-picker-project'
import type {ItemGroup} from '@github-ui/item-picker-base/shared'
import {queryOptions, useQueryClient} from '@github-ui/react-query'
import {useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'
import {
  apiGetSearchProjects,
  buildProjectPickerSearchQueryKey,
  transformSearchProjectResponseToQueryData,
  type ProjectPickerQueryData,
} from './api/api-get-search-projects'
import {parseGraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import type {SelectPanelItemInput as ItemInput} from '@primer/react'
import {TableIcon} from '@primer/octicons-react'
import {useItemPickersContext} from '@github-ui/item-picker-base/ItemPickersContext'
import {GlobalCommands} from '@github-ui/ui-commands'
import type React from 'react'
import {useKeyPress} from '@github-ui/use-key-press'

//
// ItemPicker <== ItemPickerWrapper (internal) <== ItemPickerFetcher (internal) <== ProjectPicker (exported)
//                                                                              <== BulkIssueProjectPicker (exported)

const recentGroup: ItemGroup = {groupId: 'recent', header: {title: 'Recent', variant: 'filled'}}
const repoGroup: ItemGroup = {groupId: 'repository', header: {title: 'Repository', variant: 'filled'}}
const orgGroup: ItemGroup = {groupId: 'organization', header: {title: 'Organization', variant: 'filled'}}
const selectedGroup: ItemGroup = {groupId: 'selected'}

export function searchProjectsQueryOptions({
  owner,
  repo,
  query,
  count,
}: {
  owner: string
  repo: string
  query: string
  count?: number
}) {
  return queryOptions({
    queryKey: buildProjectPickerSearchQueryKey({owner, repo, query, count}),
    queryFn: async () => {
      const responseJson = await apiGetSearchProjects({owner, repo, query, count})
      const responseData = parseGraphQLResponse(responseJson)
      return transformSearchProjectResponseToQueryData(responseData.data)
    },
  })
}

export type ProjectPickerProps = Omit<ProjectPickerBaseProps, 'onSelectionChange'> & {
  pickerId: string
  repo: string
  owner: string
  selectedProjects: ProjectPickerBaseProject[]
  onSave: (projects: ProjectPickerBaseProject[]) => void
  getSelectedProjects?: (projects: ProjectPickerBaseProject[]) => ProjectPickerBaseProject[]
}

type SelectPanelWrapperProps = {
  projectsData: ProjectPickerQueryData | null
  isLoading: boolean
} & ProjectPickerProps

const DEFAULT_SELECTED_PROJECTS: ProjectPickerBaseProject[] = []

function SelectPanelWrapper({
  projectsData: initialProjectsData,
  selectedProjects: initialSelected = DEFAULT_SELECTED_PROJECTS,
  onSave,
  anchorElement,
  insidePortal,
  anchorSide,
  owner,
  repo,
  isLoading: initialLoading = false,
  triggerOpen = false,
  readonly,
  title,
  getSelectedProjects,
  pickerId,
}: SelectPanelWrapperProps) {
  const [projectsData, setProjectsData] = useState<ProjectPickerQueryData | null>(initialProjectsData)
  const [previousProjectsData, setPreviousProjectsData] = useState(initialProjectsData)
  const [searchLoading, setSearchLoading] = useState<boolean>(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const queryClient = useQueryClient()
  const {addToast} = useToastContext()

  const [filter, setFilter] = useState('')
  const [isOpen, setIsOpen] = useState(triggerOpen)

  if (previousProjectsData !== initialProjectsData) {
    setPreviousProjectsData(initialProjectsData)
    if (filter.length === 0) {
      setProjectsData(initialProjectsData)
    }
  }

  const resetToInitialData = useEffectEvent(() => {
    setProjectsData(initialProjectsData)
  })

  const fetchSearchData = useEffectEvent(async (searchQuery: string) => {
    setSearchLoading(true)
    try {
      const data = await queryClient.fetchQuery(
        searchProjectsQueryOptions({
          repo,
          owner,
          query: searchQuery,
        }),
      )
      setProjectsData(data)
      setSearchLoading(false)
    } catch {
      setSearchLoading(false)
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({
        type: 'error',
        message: ERRORS.couldNotSearchProjects,
      })
    }
  })

  useEffect(() => {
    if (filter.length === 0) {
      resetToInitialData()
      return
    }

    const timeoutId = setTimeout(() => {
      fetchSearchData(filter)
    }, VALUES.pickerDebounceTime)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [filter])

  const recentProjects = useMemo(
    () => (projectsData?.recentOwnerProjects || []).filter(item => item.closed === false),
    [projectsData],
  )

  const repositoryProjects = useMemo(
    () => (projectsData?.repositoryProjects || []).filter(item => item.closed === false),
    [projectsData],
  )

  const organizationProjects = useMemo(
    () => (projectsData?.ownerProjects || []).filter(item => item.closed === false),
    [projectsData],
  )

  //
  // Presentation logic
  //

  const initialSelectedProjects = useMemo(
    () =>
      getSelectedProjects
        ? getSelectedProjects([...recentProjects, ...repositoryProjects, ...organizationProjects])
        : initialSelected,
    [getSelectedProjects, initialSelected, organizationProjects, recentProjects, repositoryProjects],
  )
  // Array of Project IDs
  const [selected, setSelected] = useState<string[]>(() => initialSelectedProjects.map(p => p.id))
  const [previousInitialSelected, setPreviousInitialSelected] =
    useState<ProjectPickerBaseProject[]>(initialSelectedProjects)

  if (previousInitialSelected !== initialSelectedProjects) {
    setPreviousInitialSelected(initialSelectedProjects)
    setSelected(initialSelectedProjects.map(p => p.id))
  }

  /// Convert a Project to an ItemInput object that SelectPanel can render
  const generateItemProps = useCallback(
    (groupId: string, p: ProjectPickerBaseProject): ItemInput => {
      const {title: text, id} = p

      // To prevent at-limit items being removed from the project when opening picker once the projects reached the limit
      const variant =
        p.hasReachedItemsLimit && !initialSelectedProjects.some(proj => proj.id === id)
          ? ('danger' as const)
          : ('default' as const)

      const descriptionVariant = p.hasReachedItemsLimit ? ('block' as const) : ('inline' as const)

      const item: ItemInput = {
        text,
        description: p.hasReachedItemsLimit ? `${LABELS.projectItemsLimitReached}` : undefined,
        descriptionVariant,
        id,
        groupId,
        selected: selected.includes(id),
        disabled: readonly || !p.viewerCanUpdate,
        leadingVisual: TableIcon,
        variant,
      }

      return item
    },
    [initialSelectedProjects, selected, readonly],
  )

  /// Set of all projects in the picker
  const allProjects: Record<string, ProjectPickerBaseProject[]> = useMemo(() => {
    const selectedProjectIds = initialSelectedProjects.map(p => p.id)

    return {
      selected: initialSelectedProjects,
      recent: recentProjects.filter(p => !selectedProjectIds.includes(p.id)),
      repository: repositoryProjects.filter(p => !selectedProjectIds.includes(p.id)),
      organization: organizationProjects.filter(p => !selectedProjectIds.includes(p.id)),
    }
  }, [initialSelectedProjects, recentProjects, repositoryProjects, organizationProjects])

  const projectsToSelect = useMemo(
    () =>
      Object.keys(allProjects).flatMap(pKey => {
        const projects = allProjects[pKey]
        return projects ? projects.map(p => generateItemProps(pKey, p)) : []
      }),
    [allProjects, generateItemProps],
  )

  /// Subset of projects that have been selected
  const selectedProjects = useMemo(
    () => projectsToSelect.filter(p => p.id && selected.includes(p.id.toString())),
    [selected, projectsToSelect],
  )

  /// Optional search by filtering project title
  const filteredProjects = useMemo(() => {
    // We have nothing to filter
    if (projectsToSelect.length < 1) return []

    // We have nothing to filter by
    if (filter === '') {
      return projectsToSelect
    }

    const filtered = projectsToSelect.filter(
      item => (item.text || '').toLowerCase().indexOf(filter.trim().toLowerCase()) >= 0,
    )

    // We have no filter matches
    if (filtered.length < 1) return []
    return filtered
  }, [filter, projectsToSelect])

  /// Groups to render
  const groups = useMemo(
    () =>
      [
        initialSelectedProjects.length > 0 ? selectedGroup : null,
        recentProjects.length > 0 ? recentGroup : null,
        repositoryProjects.length > 0 ? repoGroup : null,
        organizationProjects.length > 0 ? orgGroup : null,
      ].filter(group => group !== null),
    [initialSelectedProjects.length, recentProjects.length, repositoryProjects.length, organizationProjects.length],
  )

  const handleSelectionChange = useCallback(
    (selection: ItemInput[]) => {
      // Find out if the selection now excludes a previously selected item, i.e. the diff
      const diff = selectedProjects.filter(p => !selection.includes(p)).map(p => p.id)
      let projectIds: string[] = selected

      // Handle selected projects
      const selectedIds = selection
        // Filter out items that don't have IDs
        .filter(item => item.id !== undefined)
        // Pull out the IDs and cast them
        .map(item => item.id) as string[]
      projectIds = [...projectIds, ...selectedIds]

      // Handle removed projects
      if (diff.length > 0) {
        projectIds = projectIds.filter(p => !diff.includes(p))
      }

      const projectIdsSet = new Set(projectIds)
      setSelected([...projectIdsSet])
    },
    [setSelected, selectedProjects, selected],
  )

  const onCancel = useCallback(() => {
    setIsOpen(false)

    setFilter('')

    const selectionChanged =
      selected.length !== initialSelected.length ||
      selected.some(item => !initialSelected.some(item2 => item === item2.id))

    if (selectionChanged) {
      setSelected(initialSelected.map(p => (p.id ? p.id.toString() : '')))
    }
  }, [initialSelected, selected])

  const {updateOpenState} = useItemPickersContext()

  const onOpenChange = useCallback(
    (open: boolean, gesture?: string) => {
      // Fix for an issue in safari where the issue would scroll down all the way
      // when the item picker is closed with escape
      if (!open && document.activeElement instanceof HTMLElement) {
        // eslint-disable-next-line github/no-blur
        document.activeElement?.blur()
      }
      if (gesture === 'cancel') {
        return
      }
      setIsOpen(open)
      updateOpenState(pickerId, open)
      if (!open) {
        setFilter('')

        // If any of the selected projects have the danger variant (meaning that the items limit is reached), open the dialog
        const dangerItems = selectedProjects.filter(item => item.variant === 'danger')
        if (dangerItems.length > 0) {
          setIsDialogOpen(true)
        }

        // To prevent them from being checked on reopen of the picker (without reloading the page)
        const validSelected = selectedProjects
          .filter(item => item.variant !== 'danger')
          .map(p => p.id)
          .filter((id): id is string => id !== undefined)

        setSelected(validSelected)

        // Build a Map of all projects by ID for efficient lookup
        const allProjectsList: ProjectPickerBaseProject[] = Object.keys(allProjects).flatMap(pKey => {
          const projects = allProjects[pKey]
          return projects || []
        })
        const projectsMap = new Map<string, ProjectPickerBaseProject>()
        for (const project of allProjectsList) {
          projectsMap.set(project.id, project)
        }
        const uniqueProjects: ProjectPickerBaseProject[] = [...new Set(validSelected)]
          .map(id => projectsMap.get(id))
          .filter((p): p is ProjectPickerBaseProject => p !== undefined)

        if (onSave) {
          onSave(uniqueProjects)
        }
      }
    },
    [updateOpenState, pickerId, selectedProjects, onSave, allProjects],
  )

  const handleDialogClose = useCallback(() => {
    setIsDialogOpen(false)
  }, [setIsDialogOpen])

  const onSpaceKeyPress = (event: KeyboardEvent) => {
    if (isOpen) {
      const activeOption = document.querySelector(SELECTORS.activePickerOption(IDS.itemPickerRootId))

      if (activeOption) {
        const activeDataId = activeOption.getAttribute('data-id')
        const item = [...filteredProjects.values()].find(i => i.id === activeDataId)

        if (item && item.id) {
          // Don't toggle selection for disabled items
          if (item.disabled) return

          event.preventDefault()
          event.stopPropagation()
          const itemId = item.id.toString()

          setSelected(prevState => {
            const newState = [...prevState]
            const index = newState.indexOf(itemId)
            if (index > -1) {
              newState.splice(index, 1)
            } else {
              newState.push(itemId)
            }
            return newState
          })
        }
      }
    }
  }

  useKeyPress([' '], onSpaceKeyPress, {
    triggerWhenInputElementHasFocus: true,
    triggerWhenPortalIsActive: true,
  })

  return (
    <ProjectPickerBase
      anchorElement={anchorElement}
      title={title}
      items={filteredProjects}
      selectedProjects={selectedProjects}
      groups={groups}
      onOpenChange={onOpenChange}
      isDialogOpen={isDialogOpen}
      handleDialogClose={handleDialogClose}
      insidePortal={insidePortal}
      anchorSide={anchorSide}
      onCancel={onCancel}
      onSelectionChange={handleSelectionChange}
      triggerOpen={isOpen}
      isLoading={initialLoading || searchLoading}
      onFilterChange={setFilter}
      filterValue={filter}
    />
  )
}

function ItemPickerFetcher({repo, owner, ...rest}: ProjectPickerProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<ProjectPickerQueryData | null>(null)
  const queryClient = useQueryClient()

  useEffect(() => {
    const fetchInitialProjects = async () => {
      // Fetch the most recently updated projects
      try {
        const response = await queryClient.fetchQuery(searchProjectsQueryOptions({owner, repo, query: ''}))
        setData(response)
        setIsLoading(false)
        setIsError(false)
      } catch {
        setIsError(true)
        setIsLoading(false)
      }
    }
    // This useEffect is run to populate the initial list of projects to show in the picker.
    // It does not take a query into account and only relies on owner / repo
    // This useEffect has the `fetchInitialProjects` callback (which has `repo` and `owner`)
    // and `fetchKey` as its keys, so it will only be executed if one of those dependencies changes
    // - not whenever the filter changes.
    fetchInitialProjects()
  }, [fetchKey, owner, queryClient, repo])

  const anchorRef = useRef<HTMLButtonElement>(null)

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('projects'),
    anchorElement: anchorProps => rest.anchorElement(anchorProps, anchorRef),
    open: true,
  })

  if (isError) {
    return createFallbackComponent(() => setFetchKey(current => current + 1))
  }

  return <SelectPanelWrapper projectsData={data} owner={owner} repo={repo} isLoading={isLoading} {...rest} />
}

export function ProjectPicker({anchorElement, triggerOpen, ...rest}: ProjectPickerProps) {
  const [wasTriggered, setWasTriggered] = useState(triggerOpen)
  const handleGlobalCommand = useCallback(() => {
    if (!wasTriggered) {
      setWasTriggered(true)
    }
  }, [wasTriggered])

  if (!wasTriggered) {
    return (
      <>
        <GlobalCommands commands={{'item-pickers:open-projects': handleGlobalCommand}} />
        {anchorElement({
          onClick: () => {
            setWasTriggered(true)
          },
          onKeyPress: (event: React.KeyboardEvent<HTMLLIElement>) => {
            // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              setWasTriggered(true)
            }
          },
        })}
      </>
    )
  }

  return (
    <ItemPickerFetcher
      triggerOpen
      anchorElement={props => anchorElement(props)}
      {...rest}
      pickerId="list-header-projects-picker"
    />
  )
}
